/** Opening the inspector wakes scale-to-zero inference before any billable request. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSI } from '../../contexts/SIContext';
import { fetchSIJson } from './siRequest';
import type { SIModelId } from './siModels';
export type SIReadiness = 'checking' | 'warming' | 'ready' | 'unavailable';
/** 'takeoff' asks after the drawing takeoff's model, which may need waking. */
export type SIReadinessModel = SIModelId | 'takeoff';
export async function checkSIReadiness(workspaceId: string, signal: AbortSignal, projectId?: string, model?: SIReadinessModel): Promise<{ status?: string; retryAfterSeconds?: number }> {
 const { response, value: result } = await fetchSIJson<{ status?: string; retryAfterSeconds?: number }>('/si/readiness', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspaceId, ...(projectId ? { projectId } : {}), ...(model ? { model } : {}) }), signal }, 30000);
 if (!response.ok && response.status !== 429) throw new Error('Model availability could not be checked.');
 return result;
}
interface ReadinessCheck {
 key: string;
 context: string;
 controller: AbortController;
 promise: Promise<boolean>;
 settled: boolean;
 ready: boolean;
}

export function useSIReadiness(workspaceId: string | null | undefined, configured: boolean, projectId?: string, model?: SIReadinessModel) {
 const { readCached, writeCached } = useSI();
 const key = `readiness:${workspaceId ?? ''}:${projectId ?? ''}${model ? `:${model}` : ''}`;
 const context = `${key}:${configured}`;
 const contextRef = useRef(context); contextRef.current = context;
 const mountedRef = useRef(true);
 const checkRef = useRef<ReadinessCheck | null>(null);
 const [result, setResult] = useState<{ key: string; state: SIReadiness }>(() => ({ key, state: configured && readCached<boolean>(key, 30000) ? 'ready' : 'checking' }));
 const state = result.key === key && configured ? result.state : 'checking';

 const startCheck = useCallback((force = false): Promise<boolean> => {
  if (!workspaceId || !configured || !mountedRef.current || contextRef.current !== context) return Promise.resolve(false);
  const previous = checkRef.current;
  if (!force && previous?.key === key && (!previous.settled || !previous.ready)) return previous.promise;
  previous?.controller.abort();
  const controller = new AbortController();
  let resolve!: (ready: boolean) => void;
  const check: ReadinessCheck = { key, context, controller, promise: new Promise(value => { resolve = value; }), settled: false, ready: false };
  checkRef.current = check;
  const current = () => mountedRef.current && contextRef.current === context && checkRef.current === check;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const finish = (ready: boolean, nextState?: SIReadiness) => {
   if (check.settled) return;
   check.settled = true; check.ready = ready;
   if (timer) clearTimeout(timer);
   if (deadline) clearTimeout(deadline);
   if (nextState && current() && !controller.signal.aborted) {
    writeCached(key, ready);
    setResult({ key, state: nextState });
   }
   resolve(ready && current() && !controller.signal.aborted);
   controller.abort();
  };
  controller.signal.addEventListener('abort', () => finish(false), { once: true });
  deadline = setTimeout(() => finish(false, 'unavailable'), 240000);
  setResult({ key, state: 'checking' });
  const poll = async () => {
   try {
    const checked = await checkSIReadiness(workspaceId, controller.signal, projectId, model);
    if (controller.signal.aborted || !current()) { finish(false); return; }
    if (checked.status === 'ready') { finish(true, 'ready'); return; }
    if (checked.status !== 'warming') { finish(false, 'unavailable'); return; }
    writeCached(key, false);
    setResult({ key, state: 'warming' });
    const retrySeconds = Number.isFinite(checked.retryAfterSeconds) ? checked.retryAfterSeconds! : 15;
    timer = setTimeout(() => void poll(), Math.min(30, Math.max(10, retrySeconds)) * 1000);
   } catch { finish(false, controller.signal.aborted ? undefined : 'unavailable'); }
  };
  void poll();
  return check.promise;
 }, [workspaceId, configured, projectId, model, key, context, writeCached]);

 useEffect(() => {
  mountedRef.current = true;
  if (workspaceId && configured && readCached<boolean>(key, 30000)) setResult({ key, state: 'ready' });
  else if (workspaceId && configured) void startCheck();
  else setResult({ key, state: 'checking' });
  // Only abandon a check this context started. A send can start the check for
  // the next context before this effect re-runs, and aborting that one failed
  // the message with "could not start".
  const effectContext = context;
  return () => {
   mountedRef.current = false;
   if (checkRef.current?.context !== effectContext) return;
   checkRef.current.controller.abort();
   checkRef.current = null;
  };
 }, [workspaceId, configured, key, context, readCached, startCheck]);

 const recheck = useCallback(() => {
  if (!mountedRef.current || contextRef.current !== context) return;
  writeCached(key, false);
  void startCheck(true);
 }, [context, key, startCheck, writeCached]);
 const ensureReady = useCallback((signal: AbortSignal): Promise<boolean> => {
  const current = () => mountedRef.current && contextRef.current === context && !signal.aborted;
  if (!workspaceId || !configured || !current()) return Promise.resolve(false);
  if (readCached<boolean>(key, 30000)) return Promise.resolve(true);
  const pending = startCheck();
  return new Promise(resolve => {
   let settled = false;
   const finish = (ready: boolean) => {
    if (settled) return;
    settled = true;
    signal.removeEventListener('abort', onAbort);
    resolve(ready && current());
   };
   const onAbort = () => finish(false);
   signal.addEventListener('abort', onAbort, { once: true });
   if (signal.aborted) onAbort();
   void pending.then(finish);
  });
 }, [workspaceId, configured, context, key, readCached, startCheck]);
 return { state, recheck, ensureReady };
}
